import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid, PageTitle } from '../../../../components/common';
import { PermissionGuard } from '../../../../auth/components/PermissionGuard';
import { useGuiaSalidaStore } from '../store/useGuiaSalidaStore';
import { guiaSalidaPdfService } from '../services/guiaSalidaPdfService';
import type {
  GuiaSalida,
  EstadoType,
  TipoSalida,
} from '../types';
import {
  estadoLabels,
  estadoColors,
  tipoSalidaLabels,
} from '../types';

const GuiaSalidaPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const {
    guias,
    loading,
    error,
    fetchGuias,
    deleteGuia,
    clearError,
  } = useGuiaSalidaStore();

  useEffect(() => {
    fetchGuias();
  }, [fetchGuias]);

  const filteredGuias = guias.filter((g) => {
    switch (selectedTab) {
      case 0: return g.estado === 'PEN';
      case 1: return g.estado === 'APR';
      case 2: return g.estado === 'ANU';
      case 3: return true;
      default: return true;
    }
  });

  const countByEstado = (estado: EstadoType) =>
    guias.filter((g) => g.estado === estado).length;

  const handleDelete = async (id: number) => {
    if (!confirm('Eliminar esta Guia de Salida pendiente?')) return;
    await deleteGuia(id);
  };

  const handlePrint = (g: GuiaSalida) => {
    try {
      guiaSalidaPdfService.generatePdf({
        numero: g.numero,
        fechaSalida: g.fechaSalida,
        cliente: g.cliente?.razonSocial || null,
        tipoSalida: g.tipoSalida,
        placaVehiculo: g.placaVehiculo,
        observaciones: g.observaciones,
        detalles: (g.detalles || []).map((d) => ({
          origen: d.origen,
          numeroDerc: d.guiaProduccionDetalle?.guiaProduccion?.guiaRecepcion?.documento?.numeroDocumento ?? null,
          numeroGuiaProduccion: d.guiaProduccionDetalle?.idGuiaProduccion ?? d.guiaProduccionDetalle?.guiaProduccion?.id ?? 0,
          articulo: d.guiaProduccionDetalle?.articulo?.descripcion ?? null,
          presentacion: d.guiaProduccionDetalle?.articulo?.presentacion?.descripcion ?? null,
          calibre: d.guiaProduccionDetalle?.articulo?.calibre?.descripcion ?? null,
          cantidad: d.cantidad,
          kilos: d.kilos,
        })),
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
    }
  };

  const totalKilos = (g: GuiaSalida) =>
    (g.detalles || []).reduce((sum, d) => sum + (d.kilos || 0), 0);

  const columns: GridColDef[] = [
    { field: 'numero', headerName: '#', width: 90 },
    {
      field: 'fechaSalida',
      headerName: 'Fecha',
      width: 110,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString('es-ES') : '',
    },
    {
      field: 'cliente',
      headerName: 'Cliente',
      width: 220,
      valueGetter: (_value: unknown, row: GuiaSalida) =>
        row.cliente?.razonSocial || '-',
    },
    {
      field: 'tipoSalida',
      headerName: 'Tipo',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={tipoSalidaLabels[params.value as TipoSalida] || params.value}
          color={params.value === 'CONGELADO' ? 'info' : 'success'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'placaVehiculo',
      headerName: 'Placa',
      width: 110,
      valueFormatter: (value: string) => value || '-',
    },
    {
      field: 'totalKilos',
      headerName: 'Total Kg',
      width: 110,
      type: 'number',
      valueGetter: (_value: unknown, row: GuiaSalida) => totalKilos(row),
      valueFormatter: (value: number) => value?.toFixed(2),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={estadoLabels[params.value as EstadoType] || params.value}
          color={estadoColors[params.value as EstadoType] || 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as GuiaSalida;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Ver / Editar">
              <IconButton
                size="small"
                onClick={() => navigate(`/produccion/guias-salida/${row.id}`)}
                sx={{ color: '#3b82f6' }}
              >
                {row.estado === 'PEN' ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Imprimir">
              <IconButton
                size="small"
                onClick={() => handlePrint(row)}
                sx={{ color: '#0f2847' }}
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {row.estado === 'PEN' && (
              <PermissionGuard permission="PRODUCCION_EDITAR">
                <Tooltip title="Eliminar">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(row.id)}
                    sx={{ color: '#ef4444' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </PermissionGuard>
            )}
          </Box>
        );
      },
    },
  ];

  if (loading && guias.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <PageTitle
          title="Guias de Salida"
          subtitle="Liberacion de Producto B y Devolucion Interna"
        />
        <PermissionGuard permission="PRODUCCION_EDITAR">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/produccion/guias-salida/nueva')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Nueva Guia
          </Button>
        </PermissionGuard>
      </Box>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={(_e, newValue) => setSelectedTab(newValue)}
          sx={{
            px: 2,
            bgcolor: '#fff',
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.82rem',
              textTransform: 'none',
              minHeight: 48,
              color: '#64748b',
              '&.Mui-selected': { color: '#0f2847' },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(90deg, #3b82f6, #10b981)',
            },
          }}
        >
          <Tab label={`Pendientes (${countByEstado('PEN')})`} />
          <Tab label={`Aprobadas (${countByEstado('APR')})`} />
          <Tab label={`Anuladas (${countByEstado('ANU')})`} />
          <Tab label={`Lista (${guias.length})`} />
        </Tabs>
      </Paper>

      <DataGrid
        rows={filteredGuias}
        columns={columns}
        loading={loading}
        getRowId={(row: GuiaSalida) => row.id}
        pageSize={25}
      />
    </Box>
  );
};

export default GuiaSalidaPage;
